import type { Rng } from '../core/rng/Rng';
import { TILE, nearestFloor, type GeneratedMap } from '../map/MapGen';

/**
 * Environmental hazards (ME4; debris replaced blades after Sarah's first
 * playtest, 2026-07-26 — the lane strike read as nothing): both-sides
 * damage, biome-gated — hazards double as biome identity. Warrens breathe
 * poison (vents), halls shed their ceilings (falling debris marching down
 * a lane), mixed floors get at most one vent, sanctum stays clean.
 * Floors ≥ 2 only, never in the vault.
 *
 * Rolled from the per-floor `hazards` stream in a FIXED draw pattern:
 * 1 count draw ALWAYS (even for sanctum's zero), then per slot (x, y) int
 * draws. Snapping (`nearestFloor`), entrance-clearance skips, and lane
 * orientation (from local wall clearance) are all draw-free.
 *
 * Ticking is rng-free: fixed periods, phase-offset by hazard index
 * (`World.tickHazards`). Both kinds ride the telegraph→commit path for
 * player damage; enemy hits + side effects ride the hazard's `pending`
 * queue, counted down to land on the same ticks. Vents push a lingering
 * 'gas' zone — the ONE hostile zone kind (every other zone kind is
 * player-benign).
 */

export const VENT_PERIOD = 7;
export const VENT_RADIUS = 70;
export const VENT_LEAD_STEPS = 45;
export const VENT_DAMAGE = 1; // player, via the telegraph commit path
export const VENT_ENEMY_DAMAGE = 15;
export const GAS_DURATION = 3;
export const GAS_DPS = 6; // enemy dot; the player takes damagePlayer(1) on contact

export const DEBRIS_PERIOD = 6;
export const DEBRIS_LEAD_STEPS = 45;
export const DEBRIS_STEPS = 3; // impacts per collapse, marching down the lane
export const DEBRIS_STEP_DELAY = 0.35; // seconds between impacts
export const DEBRIS_RADIUS = 55;
export const DEBRIS_DAMAGE = 1; // player, via the telegraph commit path
export const DEBRIS_ENEMY_DAMAGE = 12;

export interface Hazard {
  kind: 'vent' | 'debris';
  /** World px. */
  x: number;
  y: number;
  /** Debris lane: axis angle (0 or π/2, wall-derived) + half length in px. */
  angle: number;
  halfLen: number;
  /** Seconds until the next telegraph volley fires. */
  timer: number;
  /** Committed-side strikes waiting to land (rng-free, ≤DEBRIS_STEPS). */
  pending: { x: number; y: number; commitIn: number }[];
}

/** Per-archetype hazard recipe: [kind, base count] — count = base + the 0/1 draw. */
const RECIPE: Record<GeneratedMap['archetype'], { kind: 'vent' | 'debris'; base: number; extra: boolean } | null> = {
  warrens: { kind: 'vent', base: 2, extra: true },   // 2–3 vents
  halls: { kind: 'debris', base: 1, extra: true },   // 1–2 collapse lanes
  mixed: { kind: 'vent', base: 0, extra: true },     // 0–1 vent
  sanctum: null,                                     // none — the clean biome
  vault: null,                                       // never (the ctor never calls here anyway)
};

export function rollHazards(rng: Rng, map: GeneratedMap): Hazard[] {
  // 1 count draw ALWAYS, so archetype can never shift the stream shape.
  const extraDraw = rng.int(2);
  const recipe = RECIPE[map.archetype];
  const count = recipe === null ? 0 : recipe.base + (recipe.extra ? extraDraw : 0);

  const out: Hazard[] = [];
  for (let i = 0; i < count; i++) {
    const rx = rng.int(map.w);
    const ry = rng.int(map.h);
    // Everything below the draws is draw-free (skips change counts, never draws).
    const t = nearestFloor(map.tiles, map.w, map.h, rx, ry);
    if (Math.max(Math.abs(t.x - map.entrance.x), Math.abs(t.y - map.entrance.y)) <= 4) continue;

    let angle = 0;
    let halfLen = 0;
    if (recipe!.kind === 'debris') {
      // Orientation from local wall clearance: collapse down the longer lane.
      const clear = (dx: number, dy: number): number => {
        let c = 0;
        while (c < 5 && map.tiles[(t.y + dy * (c + 1)) * map.w + (t.x + dx * (c + 1))] === 1) c++;
        return c;
      };
      const spanX = Math.min(clear(1, 0), clear(-1, 0));
      const spanY = Math.min(clear(0, 1), clear(0, -1));
      const span = Math.max(spanX, spanY);
      if (span < 2) continue; // wedged in a pocket — no lane to collapse
      angle = spanX >= spanY ? 0 : Math.PI / 2;
      halfLen = span * TILE;
    }
    out.push({
      kind: recipe!.kind,
      x: (t.x + 0.5) * TILE,
      y: (t.y + 0.5) * TILE,
      angle,
      halfLen,
      // Deterministic phase stagger: first strike lands a few seconds into
      // the floor, never all at once.
      timer: 2.5 + i * 1.7,
      pending: [],
    });
  }
  return out;
}
